import React, { useMemo, useState } from 'react';
import { Button, Form, InputGroup, Modal } from 'react-bootstrap';
import Icon from './Icon';
import { faPlus as add } from '@fortawesome/free-solid-svg-icons';
import { useData } from '@/storage/accessors';
import { Task } from '@/state/Task';
import { useReadonly } from '@/contexts/readonlyContext';

interface AddTaskToListModalProps {
    show: boolean;
    onHide: () => void;
    taskListUuid: string;
    addToStart?: boolean;
}

export const AddTaskToListModal: React.FC<AddTaskToListModalProps> = ({ show, onHide, taskListUuid, addToStart = false }) => {
    const { tasks, roles } = useData();
    const { readonly } = useReadonly();
    const [searchTerm, setSearchTerm] = useState('');

    const availableTasks = useMemo(() => {
        const allTasks = Object.values(tasks.objects).filter(t => !t.isDone && !t.taskList);
        if (!searchTerm) return allTasks;
        return allTasks.filter(t =>
            (t.description || "").toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [tasks.objects, searchTerm]);

    const handleAddTask = (taskUuid: string) => {
        if (readonly) return;
        // Получаем все задачи в списке (включая завершённые для корректной нумерации)
        const listTasks = Object.values(tasks.objects)
            .filter(t => t.taskList === taskListUuid)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        
        if (addToStart) {
            // Добавляем в начало: сдвигаем все задачи на 1 вправо
            listTasks.forEach((task, index) => {
                tasks.save(task.uuid, { order: index + 1 });
            });
            tasks.save(taskUuid, { taskList: taskListUuid, order: 0 });
        } else {
            // Добавляем в конец
            tasks.save(taskUuid, { taskList: taskListUuid, order: listTasks.length });
        }
    };

    const handleCreateNewTask = (partUuid: string) => {
        if (readonly) return;
        const part = roles.objects[partUuid];
        if (!part || !searchTerm.trim()) return;

        // Получаем все задачи в списке для корректной нумерации
        const listTasks = Object.values(tasks.objects)
            .filter(t => t.taskList === taskListUuid)
            .sort((a, b) => (a.order || 0) - (b.order || 0));

        if (addToStart) {
            // Добавляем в начало: сдвигаем все задачи на 1 вправо
            listTasks.forEach((task, index) => {
                tasks.save(task.uuid, { order: index + 1 });
            });
            // Создаём новую задачу с order: 0
            const newTask: Task = {
                uuid: crypto.randomUUID(),
                context: "",
                description: searchTerm.trim(),
                lifepart: partUuid,
                isDone: false,
                taskList: taskListUuid,
                order: 0,
            };
            tasks.create(newTask);
        } else {
            // Добавляем в конец
            const newTask: Task = {
                uuid: crypto.randomUUID(),
                context: "",
                description: searchTerm.trim(),
                lifepart: partUuid,
                isDone: false,
                taskList: taskListUuid,
                order: listTasks.length,
            };
            tasks.create(newTask);
        }
        
        setSearchTerm('');
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Добавить задачу в список</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <InputGroup className="mb-3">
                    <InputGroup.Text>
                        <Icon icon={add} style={{ fontSize: '12px', transform: 'rotate(45deg)' }} />
                    </InputGroup.Text>
                    <Form.Control
                        type="text"
                        placeholder="Поиск задачи..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </InputGroup>

                {/* Список доступных задач */}
                <div style={{ maxHeight: '250px', overflowY: 'auto', marginBottom: searchTerm ? '1rem' : 0 }}>
                    {availableTasks.length > 0 && (
                        <>
                            <h6 className="text-muted mb-2">Доступные задачи:</h6>
                            {availableTasks.map(task => {
                                const part = roles.objects[task.lifepart];
                                const partColor = part?.color || "#FFFFFF";
                                return (
                                    <div
                                        key={task.uuid}
                                        className="p-2 mb-1 border rounded bg-body-tertiary"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleAddTask(task.uuid)}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div
                                                className="rounded me-2"
                                                style={{
                                                    backgroundColor: partColor,
                                                    minWidth: 20,
                                                    height: 20
                                                }}
                                                title={part?.name || 'Не назначено'}
                                            />
                                            <small>{task.description}</small>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}

                    {availableTasks.length === 0 && searchTerm === '' && (
                        <div className="text-center text-muted py-3">
                            <small>Все задачи добавлены или завершены</small>
                        </div>
                    )}
                </div>

                {/* Кнопки создания новой задачи по частям жизни */}
                {searchTerm && searchTerm.trim() !== '' && (
                    <div>
                        <h6 className="text-muted mb-2">
                            Создать новую задачу "{searchTerm}" в:
                        </h6>
                        <div className="d-flex flex-wrap gap-2">
                            {Object.values(roles.objects)
                                .sort((a, b) => (a.order || 0) - (b.order || 0))
                                .map(part => (
                                    <Button
                                        key={part.uuid}
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleCreateNewTask(part.uuid)}
                                        className="position-relative ps-3"
                                        style={{ paddingLeft: '32px' }}
                                    >
                                        <div
                                            className="position-absolute top-50 start-0 translate-middle-y ms-2"
                                            style={{
                                                width: 12,
                                                height: 12,
                                                backgroundColor: part.color,
                                                borderRadius: '50%'
                                            }}
                                        />
                                        <span className='ms-2'>{part.name}</span>
                                    </Button>
                                ))}
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>Закрыть</Button>
            </Modal.Footer>
        </Modal>
    );
};